'use client';

import React, { useState } from 'react';
import { X, Building2, Phone, Users, Home, Layers, TrendingUp, GitBranch, Edit2 } from 'lucide-react';
import { Farm, FARM_TYPE_LABELS, FARM_TYPE_COLORS, STATUS_COLORS } from './farmsData';
import Icon from '@/components/ui/AppIcon';


interface FarmProfileModalProps {
  farm: Farm;
  onClose: () => void;
  onEditFarm: (farm: Farm) => void;
  onAddBranch: (farmId: string) => void;
}

const TABS = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'branches', label: 'Branches', icon: GitBranch },
  { id: 'houses', label: 'Houses', icon: Home },
  { id: 'flocks', label: 'Flocks', icon: Layers },
  { id: 'employees', label: 'Employees', icon: Users },
  { id: 'performance', label: 'Performance', icon: TrendingUp },
];

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2 py-2 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-400 w-36 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-gray-800 font-medium">{value || '—'}</span>
    </div>
  );
}

export default function FarmProfileModal({ farm, onClose, onEditFarm, onAddBranch }: FarmProfileModalProps) {
  const [activeTab, setActiveTab] = useState('overview');

  const occupancyPct = farm.totalCapacity > 0 ? Math.round((farm.currentBirds / farm.totalCapacity) * 100) : 0;
  const profit = farm.monthlyRevenue - farm.monthlyExpenses;
  const profitMargin = farm.monthlyRevenue > 0 ? Math.round((profit / farm.monthlyRevenue) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
              <Building2 size={18} className="text-green-700" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">{farm.farmName}</h2>
                <span className={`status-badge ${FARM_TYPE_COLORS[farm.farmType]}`}>
                  {FARM_TYPE_LABELS[farm.farmType]}
                </span>
                <span className={`status-badge ${STATUS_COLORS[farm.status]}`}>
                  {farm.status.charAt(0).toUpperCase() + farm.status.slice(1)}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">{farm.farmCode} · {farm.district}, {farm.country}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEditFarm(farm)}
              className="btn-ghost text-xs"
            >
              <Edit2 size={13} />
              Edit
            </button>
            <button onClick={onClose} className="btn-ghost p-1.5">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0.5 px-6 pt-3 border-b border-gray-100 overflow-x-auto scrollbar-thin">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-t-lg transition-all whitespace-nowrap border-b-2 ${
                  isActive
                    ? 'text-green-700 border-green-600 bg-green-50' :'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon size={13} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin p-6">

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Farm Details */}
              <div className="panel-card p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Farm Details</h3>
                <InfoRow label="Farm Code" value={farm.farmCode} />
                <InfoRow label="Farm Type" value={FARM_TYPE_LABELS[farm.farmType]} />
                <InfoRow label="Owner" value={farm.owner} />
                <InfoRow label="Opening Date" value={new Date(farm.openingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} />
                <InfoRow label="Phone" value={farm.phone} />
                <InfoRow label="Email" value={farm.email} />
              </div>

              {/* Location */}
              <div className="panel-card p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Location</h3>
                <InfoRow label="Country" value={farm.country} />
                <InfoRow label="District" value={farm.district} />
                <InfoRow label="Subcounty" value={farm.subcounty} />
                <InfoRow label="Village" value={farm.village} />
                <InfoRow label="GPS Latitude" value={farm.gpsLat} />
                <InfoRow label="GPS Longitude" value={farm.gpsLng} />
              </div>

              {/* Manager */}
              <div className="panel-card p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Farm Manager</h3>
                <InfoRow label="Manager" value={farm.manager} />
                <InfoRow label="Phone" value={farm.managerPhone} />
                <InfoRow label="Email" value={farm.managerEmail} />
              </div>

              {/* Capacity & Population */}
              <div className="panel-card p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Capacity & Population</h3>
                <InfoRow label="Total Capacity" value={farm.totalCapacity.toLocaleString() + ' birds'} />
                <InfoRow label="Current Birds" value={farm.currentBirds.toLocaleString() + ' birds'} />
                <InfoRow label="Active Houses" value={String(farm.activeHouses)} />
                <InfoRow label="Active Flocks" value={String(farm.activeFlocks)} />
                <InfoRow label="Branches" value={String(farm.branches.length)} />
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Occupancy</span>
                    <span className="font-semibold text-gray-700">{occupancyPct}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${occupancyPct}%`,
                        backgroundColor: occupancyPct > 90 ? '#dc2626' : occupancyPct > 70 ? '#d97706' : '#16a34a',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* BRANCHES TAB */}
          {activeTab === 'branches' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">{farm.branches.length} branch{farm.branches.length !== 1 ? 'es' : ''} under this farm</p>
                <button
                  onClick={() => onAddBranch(farm.id)}
                  className="btn-primary text-xs py-1.5 px-3"
                >
                  + Add Branch
                </button>
              </div>
              {farm.branches.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <GitBranch size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No branches yet. Add the first branch.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {farm.branches.map(branch => (
                    <div key={branch.id} className="panel-card p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-900">{branch.branchName}</span>
                            <span className={`status-badge ${STATUS_COLORS[branch.status]}`}>
                              {branch.status.charAt(0).toUpperCase() + branch.status.slice(1)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">{branch.branchCode} · {branch.village}, {branch.district}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { label: 'Manager', value: branch.manager },
                          { label: 'Capacity', value: branch.capacity.toLocaleString() + ' birds' },
                          { label: 'Current Birds', value: branch.currentBirds.toLocaleString() },
                          { label: 'Active Houses', value: String(branch.activeHouses) },
                        ].map(item => (
                          <div key={item.label} className="bg-gray-50 rounded-lg p-2.5">
                            <p className="text-xs text-gray-400">{item.label}</p>
                            <p className="text-sm font-semibold text-gray-800 mt-0.5">{item.value}</p>
                          </div>
                        ))}
                      </div>
                      {branch.notes && (
                        <p className="text-xs text-gray-400 mt-3 italic">{branch.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* HOUSES TAB */}
          {activeTab === 'houses' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">{farm.activeHouses} active house{farm.activeHouses !== 1 ? 's' : ''}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from({ length: farm.activeHouses }).map((_, i) => (
                  <div key={i} className="panel-card p-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <Home size={16} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">House {String.fromCharCode(65 + Math.floor(i / 2))}{(i % 2) + 1}</p>
                      <p className="text-xs text-gray-400">Deep litter · {(3000 + i * 500).toLocaleString()} capacity</p>
                    </div>
                    <span className="status-badge bg-green-100 text-green-700">Occupied</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-4 text-center">Full house management available in Poultry Houses module.</p>
            </div>
          )}

          {/* FLOCKS TAB */}
          {activeTab === 'flocks' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">{farm.activeFlocks} active flock{farm.activeFlocks !== 1 ? 's' : ''}</p>
              </div>
              <div className="space-y-3">
                {Array.from({ length: farm.activeFlocks }).map((_, i) => {
                  const types = ['Broiler', 'Layer', 'Breeder'];
                  const type = types[i % types.length];
                  const qty = [4614, 9850, 3200, 1800][i] || 2000;
                  return (
                    <div key={i} className="panel-card p-4 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                        <Layers size={16} className="text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">
                          {type === 'Broiler' ? 'BR' : type === 'Layer' ? 'LY' : 'BRD'}-2026-00{i + 1}
                        </p>
                        <p className="text-xs text-gray-400">{type} · {qty.toLocaleString()} birds · House {String.fromCharCode(65 + i)}1</p>
                      </div>
                      <span className="status-badge bg-green-100 text-green-700">Active</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-4 text-center">Full flock management available in Flock Management module.</p>
            </div>
          )}

          {/* EMPLOYEES TAB */}
          {activeTab === 'employees' && (
            <div>
              <p className="text-sm text-gray-500 mb-4">{farm.totalEmployees} employees assigned to this farm</p>
              <div className="space-y-2">
                {[
                  { name: farm.manager, role: 'Farm Manager', phone: farm.managerPhone },
                  { name: 'James Opio', role: 'Feed Manager', phone: '+256 701 111 222' },
                  { name: 'Mary Akello', role: 'Animal Health Officer', phone: '+256 772 333 444' },
                  { name: 'David Ssemakula', role: 'Store Manager', phone: '+256 756 555 666' },
                ].slice(0, Math.min(4, farm.totalEmployees)).map((emp, i) => (
                  <div key={i} className="panel-card p-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-600">
                      {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{emp.name}</p>
                      <p className="text-xs text-gray-400">{emp.role}</p>
                    </div>
                    <p className="text-xs text-gray-500">{emp.phone}</p>
                  </div>
                ))}
                {farm.totalEmployees > 4 && (
                  <p className="text-xs text-gray-400 text-center pt-2">+{farm.totalEmployees - 4} more employees. View all in Employees module.</p>
                )}
              </div>
            </div>
          )}

          {/* PERFORMANCE TAB */}
          {activeTab === 'performance' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: 'Monthly Revenue', value: `UGX ${(farm.monthlyRevenue / 1000000).toFixed(1)}M`, color: 'text-green-700', bg: 'bg-green-50' },
                  { label: 'Monthly Expenses', value: `UGX ${(farm.monthlyExpenses / 1000000).toFixed(1)}M`, color: 'text-red-600', bg: 'bg-red-50' },
                  { label: 'Net Profit', value: `UGX ${(profit / 1000000).toFixed(1)}M`, color: profit >= 0 ? 'text-green-700' : 'text-red-600', bg: profit >= 0 ? 'bg-green-50' : 'bg-red-50' },
                  { label: 'Profit Margin', value: `${profitMargin}%`, color: profitMargin >= 20 ? 'text-green-700' : 'text-orange-600', bg: profitMargin >= 20 ? 'bg-green-50' : 'bg-orange-50' },
                  { label: 'Eggs Today', value: farm.eggsTodayCount.toLocaleString(), color: 'text-yellow-700', bg: 'bg-yellow-50' },
                  { label: 'Mortality Today', value: String(farm.mortalityToday), color: farm.mortalityToday > 10 ? 'text-red-600' : 'text-gray-700', bg: farm.mortalityToday > 10 ? 'bg-red-50' : 'bg-gray-50' },
                ].map(item => (
                  <div key={item.label} className={`rounded-xl p-3 ${item.bg}`}>
                    <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                    <p className={`text-lg font-bold font-tabular ${item.color}`}>{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="panel-card p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Feed & Inventory</h3>
                <InfoRow label="Feed in Stock" value={`${farm.feedInStock.toLocaleString()} kg`} />
                <InfoRow label="Total Capacity" value={`${farm.totalCapacity.toLocaleString()} birds`} />
                <InfoRow label="Occupancy Rate" value={`${occupancyPct}%`} />
              </div>
              <p className="text-xs text-gray-400 text-center">Full financial analysis available in Finance & Analytics modules.</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
